import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { Button, Icon, StsIconName, TextBadge, Typography } from '@packages/ui/shared';
import { DateTime } from 'luxon';
import { DetailHeader } from '@web/components/detail-view';
import { SelectPopover } from './selectPopover';
import { statusVariants } from './worksheets-table';
import { useAuthState } from '@packages/contexts/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WorksheetAction } from '../types';
import { WorksheetForm } from '@packages/models/api/worksheets';
import { WorksheetModal } from './worksheet-modal';
import { worksheetsService } from '@web/services/singletons';
import { WorkSheetStatusEnum } from '@packages/models/api';
import colors from '@packages/core/styles/colors';
import { getLogger } from '@packages/core/logging';
import useWorksheetFormTranslation from '../hooks/use-worksheet-form-translation';
import useWorksheetPermission from '../hooks/use-worksheet-permission';

const logger = getLogger('admin');

interface WorksheetFormHeaderProps {
    handlePreview?: () => void;
    handleSaveAndExit: (isExit: boolean) => void;
    handleSaveButtonPress?: () => void;
    icon?: StsIconName;
    worksheetInfo?: WorksheetForm;
    worksheetDisplayVersion: number;
    worksheetFormId?: string;
    worksheetStatus: WorkSheetStatusEnum;
    isLoading: { saveAndExit: boolean; save: boolean };
    isCaseTypeLoading?: boolean;
    isPreview: boolean;
}

export const WorksheetFormHeader: FC<WorksheetFormHeaderProps> = ({
    handlePreview,
    handleSaveAndExit,
    worksheetInfo,
    worksheetDisplayVersion,
    icon,
    worksheetFormId,
    worksheetStatus,
    isLoading,
    isCaseTypeLoading,
    isPreview,
}) => {
    const { t } = useTranslation();
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(WorkSheetStatusEnum.Pending);
    const [isStatusUpdated, setIsStatusUpdated] = useState({ published: false, other: false, successfully: false });
    const [showUpdatedStatus, setShowUpdatedStatus] = useState<boolean>(false);
    const [modalBodyMessage, setModalBodyMessage] = useState<string>('');
    const [showDeleteWorksheetFormModal, setShowDeleteWorksheetFormModal] = useState<boolean>(false);
    const { modalDataTranslate, dataTranslate } = useWorksheetFormTranslation();

    const somethingWrongMsg = modalDataTranslate.commonBodyMessage.somethingWrong;
    const loading = useMemo(() => {
        return isLoading.save || isLoading.saveAndExit;
    }, [isLoading]);
    //permission for statusUpdate and deleteWorksheet api call
    const { account } = useAuthState();
    const { isEditOnlyPermission, isDeleteOnlyPermission } = useWorksheetPermission();

    const permissionsForApiCalls = useMemo(() => {
        const permissions = {
            statusUpdate: false,
            deleteWorksheet: false,
        };
        if (account) {
            const {
                techlineCapabilities: { canEditWorkSheetsTechlineCase, canDeleteWorkSheetsTechlineCase },
            } = account;
            permissions.statusUpdate = canEditWorkSheetsTechlineCase;
            permissions.deleteWorksheet = canDeleteWorkSheetsTechlineCase;
        }
        return permissions;
    }, [account]);

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const action = searchParams.get('action') as WorksheetAction;

    useEffect(() => {
        setSelectedStatus(worksheetStatus);
    }, []);

    const statusOfWorksheet = useMemo(
        () => ({
            isArchivedWorksheet: worksheetStatus === WorkSheetStatusEnum.Archived,
            isPublishedWorksheet: worksheetStatus === WorkSheetStatusEnum.Published,
            isPendingWorksheet: worksheetStatus === WorkSheetStatusEnum.Pending,
            isPilotWorksheet: worksheetStatus === WorkSheetStatusEnum.Pilot,
        }),
        [worksheetStatus]
    );

    const actionOnWorksheet = useMemo(
        () => ({
            isNewWorksheet: action === WorksheetAction.New,
            isEditWorksheet: action === WorksheetAction.Edit,
            isAddVersionWorksheet: action === WorksheetAction.AddVersion,
        }),
        [action]
    );

    const disableFields = useMemo(() => {
        const { isNewWorksheet, isAddVersionWorksheet } = actionOnWorksheet;
        const { isPublishedWorksheet, isPendingWorksheet, isArchivedWorksheet } = statusOfWorksheet;
        const isAddVersionNewWorksheet = isAddVersionWorksheet && isPendingWorksheet;
        return {
            isMenuDisable: isNewWorksheet || isAddVersionNewWorksheet || isArchivedWorksheet,
            isPublishButtonDisable: isNewWorksheet || isPublishedWorksheet || isAddVersionNewWorksheet,
            isEditButtonDisable: (isPreview && !statusOfWorksheet.isPendingWorksheet) || !isEditOnlyPermission,
            isDeleteButtonDisable: !isDeleteOnlyPermission,
        };
    }, [actionOnWorksheet, isPreview, statusOfWorksheet, isEditOnlyPermission, isDeleteOnlyPermission]);
    const relativeTime = DateTime.fromISO(worksheetInfo?.lastUpdatedTimestamp || new Date().toISOString()).toRelative();

    const actionMenu = useMemo(() => {
        const menu: { label: string; value: WorkSheetStatusEnum }[] = [];
        const archived = { label: dataTranslate.statusActions.archived, value: WorkSheetStatusEnum.Archived };
        const pilot = { label: dataTranslate.statusActions.sendToPilot, value: WorkSheetStatusEnum.Pilot };
        const addVersion = { label: dataTranslate.statusActions.addVersion, value: WorkSheetStatusEnum.AddVersion };

        const { isPendingWorksheet, isPilotWorksheet, isPublishedWorksheet } = statusOfWorksheet;

        if (isPendingWorksheet) {
            menu.push(pilot);
        } else if (isPublishedWorksheet) {
            menu.push(addVersion, archived);
        } else if (isPilotWorksheet) {
            menu.push(addVersion, archived);
        }
        return menu;
    }, [
        dataTranslate.statusActions.addVersion,
        dataTranslate.statusActions.archived,
        dataTranslate.statusActions.sendToPilot,
        statusOfWorksheet,
    ]);

    const updateWorksheetFormStatus = useCallback(
        async (status: WorkSheetStatusEnum) => {
            if (!worksheetFormId) return;

            if (!permissionsForApiCalls.statusUpdate) {
                setModalBodyMessage(modalDataTranslate.commonBodyMessage.permission.updateStatus);
                setShowUpdatedStatus(true);
                return;
            }
            // when status update to add version navigate to create new worksheet
            if (status === WorkSheetStatusEnum.AddVersion) {
                navigate(
                    `/administration/worksheet/${worksheetFormId}/edit?status=${WorkSheetStatusEnum.Pending}&action=${WorksheetAction.AddVersion}&maxVersion=${worksheetInfo?.version}`
                );
                return;
            }
            if (status === WorkSheetStatusEnum.Published) {
                setIsStatusUpdated((prev) => ({ ...prev, published: true, successfully: false }));
            } else {
                setIsStatusUpdated((prev) => ({ ...prev, other: true, successfully: false }));
            }
            const body = {
                worksheetId: worksheetFormId,
                worksheetStatus: status,
            };
            try {
                let response;

                // When status update to archived worksheet then updateWorksheetFormStatusArchived api call  other wise updateWorksheetFormStatus api call
                if (status === WorkSheetStatusEnum.Archived) {
                    response = await worksheetsService.updateWorksheetFormStatusArchived(worksheetFormId);
                } else {
                    response = await worksheetsService.updateWorksheetFormStatus(body);
                }

                if (!response.success && response.aborted) {
                    setModalBodyMessage(somethingWrongMsg);
                    return;
                } else if (!response.success) {
                    setModalBodyMessage(response.data.message || somethingWrongMsg);
                } else {
                    setModalBodyMessage(
                        modalDataTranslate.updateStatus.bodyMessage(response.data.worksheetStatus || status)
                    );
                    setSelectedStatus(status);
                    setIsStatusUpdated((prev) => ({ ...prev, successfully: true }));
                }
                setShowUpdatedStatus(true);
                setIsStatusUpdated((prev) => ({ ...prev, other: false, published: false }));
            } catch (error) {
                logger.error(`Failed to update the status of worksheet [${worksheetFormId}]`, {
                    error,
                    worksheetFormId,
                    worksheetStatus: status,
                });
                setShowUpdatedStatus(true);
                setModalBodyMessage(somethingWrongMsg);
                setIsStatusUpdated((prev) => ({ ...prev, other: false, published: false }));
            }
        },
        [
            worksheetFormId,
            permissionsForApiCalls.statusUpdate,
            modalDataTranslate.commonBodyMessage.permission.updateStatus,
            modalDataTranslate.updateStatus,
            navigate,
            worksheetInfo?.version,
            somethingWrongMsg,
        ]
    );

    const deleteWorksheetForm = useCallback(async () => {
        if (!worksheetFormId) {
            return;
        }
        try {
            const res = await worksheetsService.deleteWorksheetForm(worksheetFormId);
            if (res.success) {
                navigate('/administration/worksheets');
            }
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to delete worksheet form';
            logger.error(`Failed to delete the worksheet form [${worksheetFormId}]`, { error: e, worksheetFormId });
            throw e instanceof Error ? e : new Error(message);
        }
    }, [navigate, worksheetFormId]);

    const breadcrumbs = [
        {
            to: '/administration',
            title: t('administration:breadcrumbs.adminHub', 'Administration Hub'),
        },
        {
            to: '/administration/worksheets',
            title: dataTranslate.breadcrumbs.manageWorksheet,
        },
        {
            to: worksheetFormId ? `/administration/worksheet/${worksheetFormId}` : '/administration/worksheet/new',
            title: worksheetFormId
                ? dataTranslate.breadcrumbs.editWorksheetForm
                : dataTranslate.breadcrumbs.newWorksheetForm,
        },
    ];

    const renderStatusMenu = () => (
        <SelectPopover
            key="status-menu"
            isOpen={showStatusModal}
            options={actionMenu.map((menu) => (
                <Button
                    key={menu.value}
                    variant="ghost-gray"
                    onPress={() => updateWorksheetFormStatus(menu.value)}
                    alignTitle="left"
                    title={menu.label}
                />
            ))}
            setShow={setShowStatusModal}
        >
            <div>
                <Button
                    variant="link"
                    onPress={() => setShowStatusModal((prev) => !prev)}
                    disabled={disableFields.isMenuDisable || loading}
                    isLoading={isStatusUpdated.other}
                >
                    <Icon
                        size={30}
                        name="more-dots-vertical"
                        color="blueOne"
                        raisedContainerStyle={{ width: 22, height: 22 }}
                    />
                </Button>
            </div>
        </SelectPopover>
    );

    const renderDeleteButton = () =>
        statusOfWorksheet.isPendingWorksheet &&
        actionOnWorksheet.isEditWorksheet && (
            <TouchableOpacity
                key="delete"
                onPress={() => {
                    setModalBodyMessage(
                        permissionsForApiCalls.deleteWorksheet
                            ? modalDataTranslate.deleteWorksheet.bodyMessage
                            : modalDataTranslate.commonBodyMessage.permission.delete
                    );
                    setShowDeleteWorksheetFormModal(true);
                }}
                disabled={disableFields.isDeleteButtonDisable || loading}
            >
                <Icon
                    size={30}
                    name="trash"
                    color={disableFields.isDeleteButtonDisable ? 'grayFive' : 'blueOne'}
                    raisedContainerStyle={{ width: 22, height: 22 }}
                />
            </TouchableOpacity>
        );

    const headerRightButtons = [
        !actionOnWorksheet.isNewWorksheet && (
            <Typography variant="caption" key="save-time">
                save a {relativeTime}
            </Typography>
        ),
        renderStatusMenu(),
        <TouchableOpacity
            key="preview-button"
            onPress={handlePreview}
            disabled={disableFields.isEditButtonDisable || loading}
        >
            <Icon
                size={30}
                name={icon || 'eye'}
                color={disableFields.isEditButtonDisable ? 'grayFive' : 'blueOne'}
                raisedContainerStyle={{ width: 22, height: 22 }}
            />
        </TouchableOpacity>,
        <Button
            key="save-exit"
            variant="ghost-blue"
            onPress={() => handleSaveAndExit(true)}
            isLoading={isLoading.saveAndExit}
            disabled={isCaseTypeLoading || loading || disableFields.isEditButtonDisable}
            style={styles.primaryOutlineBtn}
        >
            {dataTranslate.adminFormList.buttons.saveAndExit}
        </Button>,
        <Button
            key="save"
            variant="primary"
            onPress={() => handleSaveAndExit(false)}
            isLoading={isLoading.save}
            disabled={isCaseTypeLoading || loading || disableFields.isEditButtonDisable}
        >
            {dataTranslate.adminFormList.buttons.save}
        </Button>,
        !statusOfWorksheet.isArchivedWorksheet && (
            <Button
                key="publish"
                isLoading={isStatusUpdated.published}
                onPress={() => updateWorksheetFormStatus(WorkSheetStatusEnum.Published)}
                disabled={disableFields.isPublishButtonDisable || loading}
            >
                {dataTranslate.adminFormList.buttons.publish}
            </Button>
        ),
        renderDeleteButton(),
    ];

    return (
        <>
            <DetailHeader
                breadcrumbs={breadcrumbs}
                subtitle={
                    <div style={styles.subtitleContainer}>
                        <div style={styles.subtitleRow}>
                            <Typography variant="h3" style={styles.headerSubtitle}>
                                {`${worksheetInfo?.displayname} ${
                                    actionOnWorksheet.isAddVersionWorksheet && statusOfWorksheet.isPendingWorksheet
                                        ? `(Adding New Version from existing v${worksheetDisplayVersion})`
                                        : `(v${worksheetDisplayVersion})`
                                }`}
                            </Typography>
                            <TextBadge variant={statusVariants[selectedStatus]} style={styles.statusBadge}>
                                {selectedStatus}
                            </TextBadge>
                        </div>
                        {worksheetInfo?.caseGroupName && (
                            <TextBadge variant="default" style={styles.groupBadge}>
                                {worksheetInfo.caseGroupName}
                            </TextBadge>
                        )}
                    </div>
                }
                hasBack
                rightSideItems={headerRightButtons}
            />

            <WorksheetModal
                show={showUpdatedStatus}
                title={modalDataTranslate.updateStatus.title}
                onHideModal={() => setShowUpdatedStatus(false)}
                bodyMessage={modalBodyMessage}
                confirmButton="Ok"
                handleAction={(accepted: boolean) => {
                    setShowUpdatedStatus(false);
                    if (isStatusUpdated.successfully) {
                        navigate('/administration/worksheets');
                    }
                }}
            />

            <WorksheetModal
                show={showDeleteWorksheetFormModal}
                title={modalDataTranslate.deleteElement.title}
                {...(permissionsForApiCalls.deleteWorksheet
                    ? { warningMessage: modalBodyMessage }
                    : { bodyMessage: modalBodyMessage })}
                onHideModal={() => setShowDeleteWorksheetFormModal(false)}
                confirmButton={modalDataTranslate.deleteElement.confirmButton}
                cancelButton={modalDataTranslate.deleteElement.cancelButton}
                handleAction={(accepted) => {
                    if (accepted && permissionsForApiCalls.deleteWorksheet) {
                        deleteWorksheetForm();
                    }
                    setShowDeleteWorksheetFormModal(false);
                }}
            />
        </>
    );
};

const styles = StyleSheet.create({
    headerSubtitle: {
        marginRight: 8,
    },
    primaryOutlineBtn: {
        borderColor: colors.blueOne,
        borderRadius: 4,
        borderWidth: 1,
    },
    subtitleContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    subtitleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    statusBadge: {
        paddingVertical: 4,
        paddingHorizontal: 12,
        borderRadius: 4,
    },
    groupBadge: {
        paddingVertical: 4,
        paddingHorizontal: 12,
        borderRadius: 4,
        alignSelf: 'flex-start',
    },
});
